import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { MorphSelect } from '@/components/ui/MorphSelect'
import { ScrollableList } from '@/components/ui/ScrollableList'
import { FloatingPanel } from '@/components/ui/FloatingPanel'
import { CloseIcon, DetailsIcon, TrashIcon } from '@/components/ui/icons'
import { useModalStore } from '@/hooks/useModalStore'
import { useDiagramStore } from '@/stores'
import type { TableIndex } from '@/types'
import { genId } from '@/utils/id'

const INDEX_TYPE_OPTIONS = [
  { value: 'btree', label: 'BTREE' },
  { value: 'hash', label: 'HASH' },
  { value: 'gin', label: 'GIN' },
  { value: 'gist', label: 'GIST' },
  { value: 'brin', label: 'BRIN' },
]

function normalizeIndexName(value: string) {
  return value.trim()
}

export function IndexPanel() {
  const { t } = useTranslation()
  const panel = useModalStore((state) => state.indexPanel)
  const close = useModalStore((state) => state.closeIndexPanel)
  const tables = useDiagramStore((state) => state.tables)
  const updateTable = useDiagramStore((state) => state.updateTable)

  const table = useMemo(
    () => tables.find((entry) => entry.id === panel.tableId) ?? null,
    [panel.tableId, tables],
  )

  const [editingId, setEditingId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [selectedColumns, setSelectedColumns] = useState<string[]>([])
  const [unique, setUnique] = useState(false)
  const [type, setType] = useState('btree')
  const [errorMessage, setErrorMessage] = useState('')
  const [pendingDelete, setPendingDelete] = useState<TableIndex | null>(null)
  const [formOpen, setFormOpen] = useState(false)

  useEffect(() => {
    if (!panel.open) return
    setFormOpen(false)
    resetForm()
  }, [panel.open, panel.tableId])

  if (!table) return null

  const indexTypeOptions = INDEX_TYPE_OPTIONS.map((option) => ({
    ...option,
    label: option.label,
  }))

  const columnOptions = table.cols
    .filter((column) => !selectedColumns.includes(column.name))
    .map((column) => ({
      value: column.name,
      label: column.name,
      description: column.type,
    }))

  const openCreateDialog = () => {
    resetForm()
    setFormOpen(true)
  }

  const beginEdit = (index: TableIndex) => {
    setEditingId(index.id)
    setName(index.name)
    setSelectedColumns(index.cols)
    setUnique(index.unique)
    setType(index.type || 'btree')
    setErrorMessage('')
    setFormOpen(true)
  }

  const resetForm = () => {
    setEditingId(null)
    setName('')
    setSelectedColumns([])
    setUnique(false)
    setType('btree')
    setErrorMessage('')
  }

  const closeForm = () => {
    setFormOpen(false)
    resetForm()
  }

  const addColumn = (columnName: string) => {
    setSelectedColumns((current) =>
      current.includes(columnName) ? current : [...current, columnName],
    )
  }

  const removeColumn = (columnName: string) => {
    setSelectedColumns((current) => current.filter((item) => item !== columnName))
  }

  const saveIndex = () => {
    const normalizedName = normalizeIndexName(name)
    if (!normalizedName) {
      setErrorMessage(t('prompts.indexNameRequired'))
      return
    }
    if (!selectedColumns.length) {
      setErrorMessage(t('prompts.indexColsRequired'))
      return
    }

    const duplicate = table.indexes.find((index) => {
      if (editingId && index.id === editingId) return false
      if (index.name === normalizedName) return true
      return (
        index.unique === unique &&
        index.type === type &&
        index.cols.join('|') === selectedColumns.join('|')
      )
    })

    if (duplicate) {
      setErrorMessage(t('details.indexDuplicate'))
      return
    }

    const nextIndex: TableIndex = {
      id: editingId || genId('idx'),
      name: normalizedName,
      cols: selectedColumns,
      unique,
      type,
    }

    const nextIndexes = editingId
      ? table.indexes.map((index) => (index.id === editingId ? nextIndex : index))
      : [...table.indexes, nextIndex]

    updateTable(table.id, { indexes: nextIndexes })
    closeForm()
  }

  return (
    <>
      <FloatingPanel
        open={panel.open}
        onClose={close}
        position={panel.position}
        title={`${t('details.indexes')}`}
        width={380}
        variant="legacy"
        anchorX="left"
        headerClassName="px-[10px] py-[8px] [&>button]:h-5 [&>button]:w-5"
        bodyClassName="px-[10px] py-[10px]"
      >
        <div className="space-y-3">
          {table.indexes.length ? (
            <ScrollableList maxVisibleItems={6} itemHeight={48} className="space-y-[5px]">
              {table.indexes.map((index) => (
                <div
                  key={index.id}
                  className="rounded-[5px] border px-[7px] py-[5px]"
                  style={{ borderColor: 'var(--border)', background: 'var(--bg-deep)' }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[10px] font-bold leading-none">{index.name}</div>
                      <div className="mt-1 flex min-w-0 items-center gap-1.5 text-[9px] leading-none" style={{ color: 'var(--text-dim)' }}>
                        <span className="shrink-0 rounded-full px-[5px] py-[1px] text-[8px] font-bold leading-none" style={{ background: 'rgba(79,70,229,.2)', color: '#818CF8' }}>
                          {index.type.toUpperCase()}
                        </span>
                        {index.unique ? (
                          <span className="shrink-0 rounded-full px-[5px] py-[1px] text-[8px] font-bold leading-none" style={{ background: 'var(--color-success-bg)', color: 'var(--color-success)' }}>
                            UNIQUE
                          </span>
                        ) : null}
                        <span className="min-w-0 truncate">{index.cols.join(', ')}</span>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        aria-label={t('common.edit')}
                        title={t('common.edit')}
                        onClick={() => beginEdit(index)}
                        className="inline-flex h-5 w-5 items-center justify-center rounded-[3px] transition-colors"
                        style={{ color: 'var(--text-dim)' }}
                      >
                        <DetailsIcon />
                      </button>
                      <button
                        type="button"
                        aria-label={t('details.deleteIndex')}
                        title={t('details.deleteIndex')}
                        onClick={() => setPendingDelete(index)}
                        className="inline-flex h-5 w-5 items-center justify-center rounded-[3px] transition-colors"
                        style={{ color: 'var(--color-danger)' }}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </ScrollableList>
          ) : (
            <div className="py-3 text-center text-[10px]" style={{ color: 'var(--text-dim)' }}>
              {t('details.noIndexes')}
            </div>
          )}

          <button
            type="button"
            onClick={openCreateDialog}
            className="flex h-8 w-full items-center justify-center rounded-lg border border-dashed text-[10px] font-medium transition-colors"
            style={{ borderColor: 'rgba(99,102,241,.28)', color: 'var(--accent)', background: 'rgba(99,102,241,.08)' }}
          >
            {t('details.addIndexButton')}
          </button>
        </div>
      </FloatingPanel>

      <Dialog
        open={!!pendingDelete}
        title={t('details.deleteIndex')}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return
          updateTable(table.id, {
            indexes: table.indexes.filter((index) => index.id !== pendingDelete.id),
          })
          setPendingDelete(null)
          if (editingId === pendingDelete.id) {
            resetForm()
          }
        }}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (!pendingDelete) return
                updateTable(table.id, {
                  indexes: table.indexes.filter((index) => index.id !== pendingDelete.id),
                })
                setPendingDelete(null)
                if (editingId === pendingDelete.id) {
                  resetForm()
                }
              }}
            >
              {t('common.delete')}
            </Button>
          </>
        )}
      >
        {t('details.deleteIndexQuestion', { name: pendingDelete?.name })}
      </Dialog>

      {formOpen ? (
        <div data-overlay-root="true" className="fixed inset-0 z-[1310] flex items-center justify-center bg-black/45 p-4">
          <div
            className="w-full max-w-md rounded-2xl border p-4 shadow-2xl"
            style={{
              background: 'var(--bg-modal)',
              borderColor: 'var(--border)',
              color: 'var(--text)',
            }}
          >
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="text-[12px] font-medium" style={{ color: 'var(--text-dim)' }}>
                {editingId ? t('common.edit') : t('details.addIndex')}
              </div>
              <button
                type="button"
                onClick={closeForm}
                aria-label={t('common.close')}
                title={t('common.close')}
                className="inline-flex h-7 w-7 items-center justify-center rounded-lg"
                style={{ color: 'var(--text-muted)' }}
              >
                <CloseIcon />
              </button>
            </div>

            <div className="space-y-3">
              {errorMessage ? (
                <div className="rounded-lg border px-3 py-2 text-[11px]" style={{ borderColor: 'var(--color-danger-border)', background: 'var(--color-danger-bg)', color: 'var(--color-danger)' }}>
                  {errorMessage}
                </div>
              ) : null}

              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t('details.indexNamePlaceholder')}
                className="morph-input"
              />

              <MorphSelect
                ariaLabel={t('common.type')}
                value={type}
                options={indexTypeOptions}
                onChange={(nextType) => {
                  if (nextType) setType(nextType)
                }}
              />

              <label className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                <input
                  type="checkbox"
                  checked={unique}
                  onChange={(event) => setUnique(event.target.checked)}
                  className="morph-checkbox"
                />
                {t('details.unique')}
              </label>

              <div className="space-y-2">
                <MorphSelect
                  ariaLabel={t('details.indexColsLabel')}
                  placeholder={t('details.indexSelectColumn')}
                  value={null}
                  options={columnOptions}
                  onChange={(columnName) => {
                    if (columnName) addColumn(columnName)
                  }}
                  emptyLabel={t('details.indexNoAvailableColumns')}
                />

                {selectedColumns.length ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedColumns.map((columnName) => (
                      <span
                        key={columnName}
                        className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px]"
                        style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}
                      >
                        <span>{columnName}</span>
                        <button
                          type="button"
                          onClick={() => removeColumn(columnName)}
                          className="inline-flex h-3.5 w-3.5 items-center justify-center"
                          aria-label={`${t('common.remove')} ${columnName}`}
                          title={`${t('common.remove')} ${columnName}`}
                          style={{ color: 'var(--text-dim)' }}
                        >
                          <CloseIcon />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed px-3 py-2 text-[11px]" style={{ borderColor: 'var(--border)', color: 'var(--text-dim)' }}>
                    {t('details.indexNoSelectedColumns')}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <Button variant="ghost" onClick={closeForm}>
                  {t('common.cancel')}
                </Button>
                <Button variant="primary" onClick={saveIndex}>
                  {editingId ? t('common.save') : t('details.addIndexButton')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
